import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, ChevronLeft, ChevronRight, Check, CheckCircle, XCircle, FileText } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = n => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(parseFloat(n) || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
const MONTHS = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];
const YEARS = Array.from({ length: 3 }, (_, i) => String(new Date().getFullYear() - 1 + i));

const STATUS = {
  draft:            { label: 'Draft',             cls: 'bg-gray-100 text-gray-700' },
  pending_approval: { label: 'Menunggu Approval', cls: 'bg-yellow-100 text-yellow-700' },
  approved:         { label: 'Disetujui',         cls: 'bg-green-100 text-green-700' },
  rejected:         { label: 'Ditolak',           cls: 'bg-red-100 text-red-700' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || { label: status, cls: 'bg-gray-100 text-gray-600' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>;
};

const calcTotal = item =>
  (parseFloat(item.rent_amount) || 0) +
  (parseFloat(item.parking_amount) || 0) +
  (parseFloat(item.laundry_amount) || 0) +
  (parseFloat(item.cleaning_amount) || 0) +
  (parseFloat(item.other_amount) || 0);

const getSummary = items => ({
  totalRooms: items.length,
  totalRent: items.reduce((s, i) => s + (parseFloat(i.rent_amount) || 0), 0),
  totalParking: items.reduce((s, i) => s + (parseFloat(i.parking_amount) || 0), 0),
  totalLaundry: items.reduce((s, i) => s + (parseFloat(i.laundry_amount) || 0), 0),
  totalCleaning: items.reduce((s, i) => s + (parseFloat(i.cleaning_amount) || 0), 0),
  totalOther: items.reduce((s, i) => s + (parseFloat(i.other_amount) || 0), 0),
  grandTotal: items.reduce((s, i) => s + (parseFloat(i.total_amount) || 0), 0),
});

export default function MonthlyBilling({ userRole }) {
  const isSuperAdmin = userRole === 'superadmin';
  const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  // ─── List state ───────────────────────────────────────────────────────────
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProp, setFilterProp] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [properties, setProperties] = useState([]);

  // ─── Navigation ──────────────────────────────────────────────────────────
  const [view, setView] = useState('list'); // 'list' | 'create' | 'detail' | 'edit'

  // ─── Create flow ──────────────────────────────────────────────────────────
  const [createStep, setCreateStep] = useState(1);
  const [selProp, setSelProp] = useState('');
  const [selMonth, setSelMonth] = useState('');
  const [selYear, setSelYear] = useState('');
  const [occupiedRooms, setOccupiedRooms] = useState([]);
  const [checkedRooms, setCheckedRooms] = useState(new Set());

  // ─── Billing items (create & edit) ───────────────────────────────────────
  const [items, setItems] = useState([]);

  // ─── Detail / edit ────────────────────────────────────────────────────────
  const [report, setReport] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  // ─── UI state ─────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ─── Data fetching ────────────────────────────────────────────────────────
  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/monthly-billing`, cfg());
      setReports(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/staff/properties`, cfg()).then(r => setProperties(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  // ─── Create helpers ───────────────────────────────────────────────────────
  const startCreate = () => {
    setSelProp(''); setSelMonth(''); setSelYear('');
    setOccupiedRooms([]); setCheckedRooms(new Set()); setItems([]);
    setCreateStep(1); setError(''); setView('create');
  };

  const loadOccupied = async prop => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/active-tenants`, cfg());
      const rooms = res.data.filter(t => t.property_name === prop);
      setOccupiedRooms(rooms);
      setCheckedRooms(new Set(rooms.map(r => r.room_number)));
    } catch { setError('Gagal memuat data kamar'); }
  };

  const handleStep1Next = async () => {
    if (!selProp || !selMonth || !selYear) { setError('Pilih properti dan periode'); return; }
    setError('');
    await loadOccupied(selProp);
    setCreateStep(2);
  };

  const handleStep2Next = () => {
    if (checkedRooms.size === 0) { setError('Pilih minimal 1 kamar'); return; }
    setError('');
    const selected = occupiedRooms.filter(r => checkedRooms.has(r.room_number));
    setItems(selected.map(r => {
      const rent = parseFloat(r.room_price) || 0;
      const parking = r.parkir ? (parseFloat(r.parkir_price) || 0) : 0;
      const laundry = r.laundry ? (parseFloat(r.laundry_price) || 0) : 0;
      const cleaning = r.cleaning ? (parseFloat(r.cleaning_price) || 0) : 0;
      return {
        room_number: r.room_number, tenant_name: r.tenant_name, tenant_phone: r.tenant_phone,
        check_in_date: r.check_in_date, rent_amount: rent, parking_amount: parking,
        laundry_amount: laundry, cleaning_amount: cleaning, other_amount: 0,
        other_description: '', is_prorata: false, total_amount: rent + parking + laundry + cleaning,
      };
    }));
    setCreateStep(3);
  };

  const updateItem = (idx, field, value) => {
    setItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      next[idx].total_amount = calcTotal(next[idx]);
      return next;
    });
  };

  const periodLabel = () => `${MONTHS[parseInt(selMonth) - 1]} ${selYear}`;

  // ─── Save / submit ────────────────────────────────────────────────────────
  const handleCreate = async newStatus => {
    setSaving(true); setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/admin/monthly-billing`, {
        property_name: selProp, period: periodLabel(), items, status: newStatus,
      }, cfg());
      setSuccess(newStatus === 'pending_approval' ? 'Laporan diajukan!' : 'Draft tersimpan!');
      setView('list'); fetchReports();
    } catch (e) { setError(e.response?.data?.detail || 'Gagal menyimpan'); }
    finally { setSaving(false); }
  };

  const handleUpdate = async newStatus => {
    setSaving(true); setError('');
    try {
      await axios.put(`${BACKEND_URL}/api/admin/monthly-billing/${report.id}`, { items, status: newStatus }, cfg());
      setSuccess(newStatus === 'pending_approval' ? 'Laporan diajukan!' : 'Draft tersimpan!');
      setView('list'); setReport(null); fetchReports();
    } catch (e) { setError(e.response?.data?.detail || 'Gagal menyimpan'); }
    finally { setSaving(false); }
  };

  const handleApprove = async () => {
    if (!window.confirm('Setujui laporan ini?')) return;
    try {
      await axios.put(`${BACKEND_URL}/api/admin/monthly-billing/${report.id}/approve`, {}, cfg());
      setSuccess('Laporan disetujui!'); setView('list'); setReport(null); fetchReports();
    } catch (e) { setError(e.response?.data?.detail || 'Gagal'); }
  };

  const handleReject = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/monthly-billing/${report.id}/reject`, { reason: rejectReason }, cfg());
      setSuccess('Laporan ditolak.'); setShowRejectModal(false); setRejectReason('');
      setView('list'); setReport(null); fetchReports();
    } catch (e) { setError(e.response?.data?.detail || 'Gagal'); }
  };

  const openDetail = async r => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/monthly-billing/${r.id}`, cfg());
      setReport(res.data); setItems(res.data.items || []); setView('detail');
    } catch { setError('Gagal memuat detail'); }
  };

  const openEdit = async r => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/monthly-billing/${r.id}`, cfg());
      setReport(res.data); setItems(res.data.items || []); setView('edit');
    } catch { setError('Gagal memuat data'); }
  };

  // ─── Billing table (shared between create step 3 and edit) ────────────────
  const BillingTable = ({ editable = true }) => {
    const sum = getSummary(items);
    return (
      <div className="space-y-4">
        <div className="overflow-x-auto rounded-xl border border-white/40">
          <table className="w-full text-xs min-w-[900px]">
            <thead>
              <tr className="bg-white/40 border-b border-white/30">
                {['Kamar','Tenant','Check-in','Sewa','Parkir','Laundry','Kebersihan','Lain-lain','Total','Prorata'].map(h => (
                  <th key={h} className={`px-3 py-2 font-semibold text-isk-muted ${h==='Total'||h==='Sewa'||h==='Parkir'||h==='Laundry'||h==='Kebersihan'||h==='Lain-lain' ? 'text-right' : h==='Prorata' ? 'text-center' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} className="border-b border-white/10 hover:bg-white/20">
                  <td className="px-3 py-2 font-bold text-isk-text">{item.room_number}</td>
                  <td className="px-3 py-2">
                    <p className="font-medium">{item.tenant_name}</p>
                    <p className="text-isk-muted">{item.tenant_phone}</p>
                  </td>
                  <td className="px-3 py-2 text-isk-muted whitespace-nowrap">{fmtDate(item.check_in_date)}</td>
                  <td className="px-3 py-2 text-right">
                    {editable ? (
                      <input type="text" inputMode="numeric" value={item.rent_amount}
                        disabled={!item.is_prorata}
                        onChange={e => updateItem(idx, 'rent_amount', parseFloat(e.target.value.replace(/[^0-9]/g, '')) || 0)}
                        className={`w-28 text-right text-xs rounded-lg px-2 py-1 border ${item.is_prorata ? 'border-isk-teal bg-white' : 'border-transparent bg-transparent font-medium text-isk-text'}`}
                      />
                    ) : (
                      <span className="font-medium">{fmt(item.rent_amount)}{item.is_prorata && <span className="ml-1 text-orange-500 font-bold text-xs">P</span>}</span>
                    )}
                  </td>
                  {['parking_amount','laundry_amount','cleaning_amount','other_amount'].map(field => (
                    <td key={field} className="px-3 py-2 text-right">
                      {editable ? (
                        <input type="text" inputMode="numeric" value={item[field]}
                          onChange={e => updateItem(idx, field, parseFloat(e.target.value.replace(/[^0-9]/g, '')) || 0)}
                          className="w-24 text-right text-xs rounded-lg px-2 py-1 border border-gray-200 bg-white"
                        />
                      ) : <span className="font-medium">{fmt(item[field])}</span>}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right font-bold text-isk-teal whitespace-nowrap">{fmt(item.total_amount)}</td>
                  <td className="px-3 py-2 text-center">
                    {editable ? (
                      <input type="checkbox" checked={!!item.is_prorata}
                        onChange={e => updateItem(idx, 'is_prorata', e.target.checked)}
                        className="w-4 h-4 accent-isk-teal cursor-pointer" />
                    ) : item.is_prorata ? <span className="text-orange-500 font-bold">✓</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Summary */}
        <div className="glass-card rounded-2xl p-4">
          <h4 className="text-sm font-bold text-isk-text mb-3">Ringkasan</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            {[
              ['Total Kamar', sum.totalRooms + ' kamar'],
              ['Total Sewa', fmt(sum.totalRent)],
              ['Total Parkir', fmt(sum.totalParking)],
              ['Total Laundry', fmt(sum.totalLaundry)],
              ['Total Kebersihan', fmt(sum.totalCleaning)],
              ['Total Lainnya', fmt(sum.totalOther)],
              ['Grand Total', fmt(sum.grandTotal)],
            ].map(([lbl, val]) => (
              <div key={lbl} className="p-2 rounded-xl bg-white/50 border border-white/40 text-xs">
                <p className="text-isk-muted">{lbl}</p>
                <p className="font-bold text-isk-text mt-0.5">{val}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ─── Alerts ───────────────────────────────────────────────────────────────
  const Alerts = () => (
    <>
      {success && <div className="p-3 rounded-xl bg-isk-teal/5 border border-isk-teal/20 text-isk-teal text-sm">{success}</div>}
      {error && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
    </>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // VIEW: CREATE
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === 'create') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button onClick={() => setView('list')} className="text-isk-muted hover:text-isk-text"><ChevronLeft className="w-5 h-5" /></button>
          <div>
            <h2 className="text-lg font-bold text-isk-text">Buat Tagihan Bulanan</h2>
            <p className="text-xs text-isk-muted">Langkah {createStep} dari 3</p>
          </div>
        </div>

        {/* Step indicators */}
        <div className="flex gap-2">
          {['Properti & Periode','Pilih Kamar','Detail Tagihan'].map((s, i) => (
            <div key={i} className={`flex-1 py-1.5 px-2 rounded-full text-xs text-center font-medium transition-colors ${createStep === i+1 ? 'bg-isk-teal text-white' : createStep > i+1 ? 'bg-isk-teal/20 text-isk-teal' : 'bg-white/40 text-isk-muted'}`}>{s}</div>
          ))}
        </div>

        <Alerts />

        {/* Step 1 */}
        {createStep === 1 && (
          <div className="glass-card rounded-2xl p-6 space-y-4 max-w-md">
            <div>
              <label className="text-sm font-semibold text-isk-text">Properti</label>
              <Select value={selProp} onValueChange={setSelProp}>
                <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Pilih properti" /></SelectTrigger>
                <SelectContent>{properties.map(p => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold text-isk-text">Periode Tagihan</label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Select value={selMonth} onValueChange={setSelMonth}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Bulan" /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m,i) => <SelectItem key={m} value={String(i+1).padStart(2,'0')}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={selYear} onValueChange={setSelYear}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder="Tahun" /></SelectTrigger>
                  <SelectContent>{YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleStep1Next} className="w-full bg-isk-teal hover:bg-isk-dark text-white rounded-full">
              Lanjut <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Step 2 */}
        {createStep === 2 && (
          <div className="glass-card rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-isk-text">{selProp} · {MONTHS[parseInt(selMonth)-1]} {selYear}</p>
                <p className="text-xs text-isk-muted mt-0.5">{occupiedRooms.length} kamar terisi · {checkedRooms.size} dipilih</p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setCheckedRooms(new Set(occupiedRooms.map(r => r.room_number)))} className="text-xs text-isk-teal hover:underline">Pilih Semua</button>
                <button onClick={() => setCheckedRooms(new Set())} className="text-xs text-isk-muted hover:underline">Kosongkan</button>
              </div>
            </div>
            {occupiedRooms.length === 0 ? (
              <p className="text-sm text-isk-muted text-center py-8">Tidak ada kamar terisi di properti ini</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {occupiedRooms.sort((a,b) => a.room_number.localeCompare(b.room_number, undefined, {numeric:true})).map(room => {
                  const sel = checkedRooms.has(room.room_number);
                  return (
                    <div key={room.room_number} onClick={() => {
                      const s = new Set(checkedRooms);
                      sel ? s.delete(room.room_number) : s.add(room.room_number);
                      setCheckedRooms(s);
                    }} className={`p-3 rounded-xl border cursor-pointer transition-all ${sel ? 'border-isk-teal bg-isk-teal/5 shadow-sm' : 'border-white/40 bg-white/30 hover:bg-white/50'}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-bold text-isk-text">Kamar {room.room_number}</p>
                          <p className="text-xs text-isk-muted truncate mt-0.5">{room.tenant_name}</p>
                          <p className="text-xs text-isk-muted">{room.tenant_phone}</p>
                          <p className="text-xs font-semibold text-isk-teal mt-1">{fmt(room.room_price)}/bln</p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${sel ? 'border-isk-teal bg-isk-teal' : 'border-gray-300 bg-white'}`}>
                          {sel && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setCreateStep(1)} className="rounded-full border-isk-teal/30 text-isk-teal"><ChevronLeft className="w-4 h-4 mr-1" /> Kembali</Button>
              <Button onClick={handleStep2Next} disabled={checkedRooms.size === 0} className="bg-isk-teal hover:bg-isk-dark text-white rounded-full">
                Lanjut ({checkedRooms.size} kamar) <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 */}
        {createStep === 3 && (
          <div className="glass-card rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="font-semibold text-isk-text">{selProp} · {periodLabel()}</p>
                <p className="text-xs text-isk-muted">{items.length} kamar · Klik checkbox Prorata untuk edit harga sewa</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setCreateStep(2)} className="rounded-full text-xs border-isk-teal/30 text-isk-teal">
                <ChevronLeft className="w-3 h-3 mr-1" /> Ubah Kamar
              </Button>
            </div>
            <BillingTable editable={true} />
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => handleCreate('draft')} disabled={saving} className="border-isk-teal/30 text-isk-teal hover:bg-isk-teal hover:text-white rounded-full">
                {saving ? 'Menyimpan...' : 'Simpan Draft'}
              </Button>
              <Button onClick={() => handleCreate('pending_approval')} disabled={saving} className="bg-isk-teal hover:bg-isk-dark text-white rounded-full">
                {saving ? 'Mengajukan...' : 'Ajukan'}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // VIEW: DETAIL
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === 'detail' && report) {
    const canEdit = !isSuperAdmin && (report.status === 'draft' || report.status === 'rejected');
    const detailItems = report.items || [];
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => { setView('list'); setReport(null); }} className="text-isk-muted hover:text-isk-text"><ChevronLeft className="w-5 h-5" /></button>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-isk-text">{report.property_name} · {report.period}</h2>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <StatusBadge status={report.status} />
              <span className="text-xs text-isk-muted">Dibuat: {fmtDate(report.created_at)}</span>
              {report.submitted_at && <span className="text-xs text-isk-muted">· Diajukan: {fmtDate(report.submitted_at)}</span>}
              {report.approved_at && <span className="text-xs text-green-600">· Disetujui: {fmtDate(report.approved_at)}</span>}
            </div>
          </div>
          {canEdit && <Button size="sm" onClick={() => openEdit(report)} className="bg-isk-teal text-white rounded-full text-xs">Edit Laporan</Button>}
        </div>

        <Alerts />

        {report.status === 'rejected' && report.rejection_reason && (
          <div className="p-3 rounded-xl bg-red-50 border border-red-200">
            <p className="text-xs font-semibold text-red-700 mb-1">Alasan Penolakan</p>
            <p className="text-xs text-red-600">{report.rejection_reason}</p>
          </div>
        )}

        <div className="glass-card rounded-2xl p-4">
          <BillingTable editable={false} />
        </div>

        {/* Superadmin actions */}
        {isSuperAdmin && report.status === 'pending_approval' && (
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowRejectModal(true)} className="flex-1 border-red-300 text-red-600 hover:bg-red-50 rounded-full">
              <XCircle className="w-4 h-4 mr-1" /> Tolak
            </Button>
            <Button onClick={handleApprove} className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-full">
              <CheckCircle className="w-4 h-4 mr-1" /> Setujui
            </Button>
          </div>
        )}

        {showRejectModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
              <h3 className="font-bold text-isk-text mb-3">Tolak Laporan</h3>
              <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                placeholder="Alasan penolakan (opsional)"
                className="w-full border border-gray-200 rounded-xl p-3 text-sm min-h-[80px] focus:outline-none focus:border-isk-teal resize-none mb-4" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowRejectModal(false)} className="flex-1 rounded-full">Batal</Button>
                <Button onClick={handleReject} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full">Tolak</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // VIEW: EDIT
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === 'edit' && report) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button onClick={() => openDetail(report)} className="text-isk-muted hover:text-isk-text"><ChevronLeft className="w-5 h-5" /></button>
          <div>
            <h2 className="text-lg font-bold text-isk-text">Edit Tagihan · {report.property_name} · {report.period}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <StatusBadge status={report.status} />
              <span className="text-xs text-isk-muted">Klik checkbox Prorata untuk edit harga sewa</span>
            </div>
          </div>
        </div>

        <Alerts />

        {report.status === 'rejected' && report.rejection_reason && (
          <div className="p-3 rounded-xl bg-red-50 border border-red-200">
            <p className="text-xs font-semibold text-red-700 mb-1">Alasan Penolakan Sebelumnya</p>
            <p className="text-xs text-red-600">{report.rejection_reason}</p>
          </div>
        )}

        <div className="glass-card rounded-2xl p-6 space-y-4">
          <BillingTable editable={true} />
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => handleUpdate('draft')} disabled={saving} className="border-isk-teal/30 text-isk-teal hover:bg-isk-teal hover:text-white rounded-full">
              {saving ? 'Menyimpan...' : 'Simpan Draft'}
            </Button>
            <Button onClick={() => handleUpdate('pending_approval')} disabled={saving} className="bg-isk-teal hover:bg-isk-dark text-white rounded-full">
              {saving ? 'Mengajukan...' : 'Ajukan'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // VIEW: LIST
  // ═══════════════════════════════════════════════════════════════════════════
  const statusCounts = {
    all: reports.length,
    draft: reports.filter(r => r.status === 'draft').length,
    pending_approval: reports.filter(r => r.status === 'pending_approval').length,
    approved: reports.filter(r => r.status === 'approved').length,
    rejected: reports.filter(r => r.status === 'rejected').length,
  };
  const propOptions = [...new Set(reports.map(r => r.property_name).filter(Boolean))].sort();
  const filtered = reports.filter(r => {
    if (filterProp !== 'all' && r.property_name !== filterProp) return false;
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-isk-text">Tagihan Bulanan</h2>
          <p className="text-xs text-isk-muted mt-0.5">Laporan tagihan bulanan per properti</p>
        </div>
        {!isSuperAdmin && (
          <Button onClick={startCreate} className="bg-isk-teal hover:bg-isk-dark text-white rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Buat Tagihan Bulanan
          </Button>
        )}
      </div>

      <Alerts />

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="flex gap-1 bg-white/40 rounded-xl p-1 flex-wrap">
          {[['all','Semua'],['draft','Draft'],['pending_approval','Menunggu'],['approved','Disetujui'],['rejected','Ditolak']].map(([k,l]) => (
            <button key={k} onClick={() => setFilterStatus(k)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterStatus===k ? 'bg-isk-teal text-white shadow' : 'text-isk-muted hover:text-isk-text'}`}>
              {l} ({statusCounts[k] ?? 0})
            </button>
          ))}
        </div>
        <Select value={filterProp} onValueChange={setFilterProp}>
          <SelectTrigger className="w-44 rounded-full text-xs h-8"><SelectValue placeholder="Semua Properti" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Properti</SelectItem>
            {propOptions.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="glass-card rounded-2xl overflow-hidden border border-white/40">
        <div className="hidden sm:grid grid-cols-[2fr_1.5fr_1.5fr_1.5fr_1fr_auto] gap-3 px-4 py-2.5 bg-white/40 border-b border-white/40 text-xs font-semibold text-isk-muted">
          <span>Periode</span><span>Properti</span><span>Kamar</span><span>Total</span><span>Status</span><span></span>
        </div>
        {loading ? (
          <div className="py-12 text-center"><div className="w-8 h-8 border-4 border-isk-teal border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-isk-muted text-sm">
            <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
            Belum ada laporan tagihan bulanan
          </div>
        ) : (
          <div className="divide-y divide-white/20">
            {filtered.map(r => {
              const canEdit = !isSuperAdmin && (r.status === 'draft' || r.status === 'rejected');
              return (
                <div key={r.id} className="grid grid-cols-1 sm:grid-cols-[2fr_1.5fr_1.5fr_1.5fr_1fr_auto] gap-2 sm:gap-3 px-4 py-3 items-center hover:bg-white/30 transition-colors">
                  <div>
                    <p className="font-semibold text-isk-text text-sm">{r.period}</p>
                    <p className="text-xs text-isk-muted sm:hidden">{r.property_name} · {r.total_rooms} dari {r.total_occupied_rooms} kamar</p>
                  </div>
                  <p className="hidden sm:block text-sm text-isk-muted">{r.property_name}</p>
                  <p className="hidden sm:block text-sm text-isk-text">{r.total_rooms} dari {r.total_occupied_rooms} kamar</p>
                  <p className="hidden sm:block text-sm font-semibold text-isk-teal">{fmt(r.total_amount)}</p>
                  <div className="hidden sm:block"><StatusBadge status={r.status} /></div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" onClick={() => openDetail(r)}
                      className="bg-isk-teal/10 hover:bg-isk-teal hover:text-white text-isk-teal rounded-full text-xs h-7 px-3 transition-colors">
                      Detail <ChevronRight className="w-3 h-3 ml-0.5" />
                    </Button>
                    {canEdit && (
                      <Button size="sm" variant="outline" onClick={() => openEdit(r)}
                        className="border-isk-teal/30 text-isk-teal hover:bg-isk-teal hover:text-white rounded-full text-xs h-7 px-2 transition-colors">
                        Edit
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
